"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSecondaryButtonClass,
} from "@/lib/cinematicModalStyles";
import { getLanguageJourneyChapterGroups } from "@/lib/languageJourneyClient";
import {
  SPANISH_CHALLENGE_LEVEL_LABELS,
  SPANISH_CORRECTION_TIMING_LABELS,
  SPANISH_GOAL_LABELS,
  SPANISH_LANGUAGE_SUPPORT_LABELS,
  SPANISH_STUCK_STRATEGY_LABELS,
  SPANISH_TEACHING_APPROACH_LABELS,
  type SpanishJourneyGoalProfile,
  type SpanishJourneyPlan,
  getSpanishJourney,
  getSpanishJourneyAbilityForChapter,
  getSpanishJourneyPlanShape,
  getSpanishJourneyStartingChapter,
} from "@/lib/spanishJourneyPlannerClient";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Gauge,
  Map as MapIcon,
  RefreshCw,
  Route,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Option<T extends string> = { value: T; label: string; description: string };

const GOAL_OPTIONS: Option<SpanishJourneyGoalProfile["goal"]>[] = [
  {
    value: "everyday",
    label: "Everyday conversations",
    description: "Speak naturally in daily life.",
  },
  {
    value: "travel",
    label: "Travel confidently",
    description: "Handle plans, questions and problems.",
  },
  {
    value: "work",
    label: "Work in Spanish",
    description: "Discuss projects, decisions and people.",
  },
  {
    value: "relationships",
    label: "Connect with people",
    description: "Talk with family, friends or a partner.",
  },
  {
    value: "media",
    label: "Understand Spanish media",
    description: "Follow podcasts, shows and articles.",
  },
  { value: "study", label: "Study in Spanish", description: "Build precise academic expression." },
];

const TEACHING_APPROACH_OPTIONS: Option<SpanishJourneyGoalProfile["teachingApproach"]>[] = [
  {
    value: "guided_discovery",
    label: "Guide me with questions",
    description: "Give clues and let me discover the pattern.",
  },
  {
    value: "explain_then_practice",
    label: "Explain, then practise",
    description: "Give me a short explanation before I speak.",
  },
  {
    value: "examples_first",
    label: "Examples first",
    description: "Show useful examples, then help me notice why they work.",
  },
  {
    value: "challenge_me",
    label: "Challenge me",
    description: "Use less support and make me work the answer out.",
  },
];

const STUCK_STRATEGY_OPTIONS: Option<SpanishJourneyGoalProfile["stuckStrategy"]>[] = [
  { value: "hint", label: "Give me a hint", description: "One useful clue, not the answer." },
  {
    value: "smaller_steps",
    label: "Use smaller steps",
    description: "Break the same idea into manageable pieces.",
  },
  {
    value: "another_example",
    label: "Show another example",
    description: "Teach the pattern again with different language.",
  },
  {
    value: "realistic_situation",
    label: "Make it realistic",
    description: "Put the same concept into a practical situation.",
  },
  {
    value: "retry_before_help",
    label: "Let me retry",
    description: "Give me another attempt before stepping in.",
  },
];

const EMPTY_PROFILE: SpanishJourneyGoalProfile = {
  goal: "everyday",
  ability: "starting_out",
  teachingApproach: "guided_discovery",
  stuckStrategy: "hint",
  correctionTiming: "after_attempt",
  languageSupport: "balanced",
  challengeLevel: "focused",
  timeAvailable: "one_month",
  goalDetail: "",
};

function StartingPointPicker({
  profile,
  onChange,
}: {
  profile: SpanishJourneyGoalProfile;
  onChange: (chapterId: string, ability: SpanishJourneyGoalProfile["ability"]) => void;
}) {
  const journey = getSpanishJourney();
  const groups = getLanguageJourneyChapterGroups(journey);
  const startingChapter = getSpanishJourneyStartingChapter(profile);
  const startingGroup = groups.find((group) =>
    group.chapters.some((chapter) => chapter.id === startingChapter.id)
  );
  const [openGroupId, setOpenGroupId] = useState(startingGroup?.id || groups[0]?.id || "");
  const openGroup = groups.find((group) => group.id === openGroupId) || startingGroup || groups[0];
  const requiredSessions = getSpanishJourneyPlanShape(profile).sessions;
  const latestStartIndex = journey.chapters.length - requiredSessions;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-orange-300/25 bg-orange-500/[0.055] p-3">
        <p className="text-[10px] font-black uppercase text-orange-200/65">
          Your route starts here
        </p>
        <p className="mt-1 text-sm font-black text-white">
          Chapter {startingChapter.chapterNumber}: {startingChapter.title}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-white/45">
          {startingChapter.newConcept || startingChapter.objective}
        </p>
      </div>

      <div>
        <p className="mb-2 text-[10px] font-black uppercase text-white/40">1. Choose a unit</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {groups.map((group, index) => {
            const selected = group.id === openGroup?.id;
            const first = group.chapters[0];
            const last = group.chapters[group.chapters.length - 1];
            return (
              <button
                key={group.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setOpenGroupId(group.id)}
                className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  selected
                    ? "border-orange-300/50 bg-orange-500/10"
                    : "border-white/10 bg-white/[0.025] hover:bg-white/[0.055]"
                }`}
              >
                <span className="block text-[10px] font-black uppercase text-orange-200/60">
                  Unit {index + 1} · Chapters {first?.chapterNumber}–{last?.chapterNumber}
                </span>
                <span className="mt-0.5 block truncate text-xs font-bold text-white/75">
                  {group.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {openGroup && (
        <div>
          <p className="mb-2 text-[10px] font-black uppercase text-white/40">
            2. Choose your first chapter
          </p>
          <div className="grid gap-2">
            {openGroup.chapters.map((chapter) => {
              const selected = chapter.id === startingChapter.id;
              const chapterIndex = journey.chapters.findIndex(
                (candidate) => candidate.id === chapter.id
              );
              const unavailable = chapterIndex > latestStartIndex;
              return (
                <button
                  key={chapter.id}
                  type="button"
                  aria-label={`Start at Chapter ${chapter.chapterNumber}: ${chapter.title}`}
                  aria-pressed={selected}
                  disabled={unavailable}
                  onClick={() => {
                    onChange(chapter.id, getSpanishJourneyAbilityForChapter(chapter));
                  }}
                  className={`flex min-h-12 items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
                    selected
                      ? "border-orange-300/55 bg-orange-500/10"
                      : "border-white/10 bg-white/[0.025] hover:bg-white/[0.055]"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-black ${
                      selected
                        ? "border-orange-200 bg-orange-500 text-white"
                        : "border-white/15 text-white/50"
                    }`}
                  >
                    {chapter.chapterNumber}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-bold text-white/80">{chapter.title}</span>
                    <span className="mt-0.5 block truncate text-[10px] text-white/40">
                      {unavailable
                        ? "Start at Chapter 85 or earlier for a 6-session route"
                        : chapter.newConcept || chapter.objective}
                    </span>
                  </span>
                  {selected && (
                    <Check className="ml-auto h-4 w-4 text-orange-200" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceGrid<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`min-h-[4.75rem] rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
              selected
                ? "border-orange-300/60 bg-orange-500/12"
                : "border-white/10 bg-white/[0.025] hover:bg-white/[0.055]"
            }`}
          >
            <span className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-sm font-bold text-white">{option.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-white/50">
                  {option.description}
                </span>
              </span>
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                  selected
                    ? "border-orange-300 bg-orange-500 text-white"
                    : "border-white/20 text-transparent"
                }`}
              >
                <Check className="h-3 w-3" aria-hidden="true" />
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PreferenceChoices<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-black uppercase text-white/40">{label}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={`flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
                selected
                  ? "border-orange-300/55 bg-orange-500/10 text-white"
                  : "border-white/10 bg-white/[0.025] text-white/55 hover:bg-white/[0.055] hover:text-white"
              }`}
            >
              <span>{option.label}</span>
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  selected
                    ? "border-orange-300 bg-orange-500 text-white"
                    : "border-white/20 text-transparent"
                }`}
              >
                <Check className="h-2.5 w-2.5" aria-hidden="true" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SpanishJourneyPlannerModal({
  isOpen,
  onClose,
  onBack,
  existingPlan,
  generating,
  saving,
  error,
  onGenerate,
  onSave,
  onOpenPath,
}: {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  existingPlan: SpanishJourneyPlan | null;
  generating: boolean;
  saving: boolean;
  error: string | null;
  onGenerate: (profile: SpanishJourneyGoalProfile) => Promise<SpanishJourneyPlan>;
  onSave: (plan: SpanishJourneyPlan) => Promise<void>;
  onOpenPath: () => void;
}) {
  const [step, setStep] = useState(existingPlan ? 4 : 0);
  const [profile, setProfile] = useState<SpanishJourneyGoalProfile>(
    existingPlan?.profile || EMPTY_PROFILE
  );
  const [draft, setDraft] = useState<SpanishJourneyPlan | null>(existingPlan);
  const [editingCoaching, setEditingCoaching] = useState(false);
  const [editingStartingChapterOnly, setEditingStartingChapterOnly] = useState(false);
  const [returnToReviewOnFirstBack, setReturnToReviewOnFirstBack] = useState(false);
  const journey = useMemo(() => getSpanishJourney(), []);
  const chapterById = useMemo(
    () => new Map(journey.chapters.map((chapter) => [chapter.id, chapter])),
    [journey]
  );

  useEffect(() => {
    if (!isOpen) return;

    setStep(existingPlan ? 4 : 0);
    setProfile(existingPlan?.profile || EMPTY_PROFILE);
    setDraft(existingPlan);
    setEditingCoaching(false);
    setEditingStartingChapterOnly(false);
    setReturnToReviewOnFirstBack(false);
  }, [existingPlan, isOpen]);

  const beginAgain = () => {
    setProfile(existingPlan?.profile || EMPTY_PROFILE);
    setEditingCoaching(false);
    setEditingStartingChapterOnly(false);
    setReturnToReviewOnFirstBack(true);
    setStep(0);
  };

  const openStartingChapter = () => {
    setProfile(draft?.profile || existingPlan?.profile || EMPTY_PROFILE);
    setEditingStartingChapterOnly(true);
    setStep(1);
  };

  const handleBack = () => {
    if (editingStartingChapterOnly) {
      setEditingStartingChapterOnly(false);
      setProfile(draft?.profile || existingPlan?.profile || EMPTY_PROFILE);
      setStep(4);
      return;
    }
    if (step === 0) {
      if (returnToReviewOnFirstBack && draft) {
        setReturnToReviewOnFirstBack(false);
        setProfile(draft.profile);
        setStep(4);
        return;
      }
      (onBack || onClose)();
      return;
    }
    setStep((current) => Math.max(0, current - 1));
  };

  const createPlan = async () => {
    const next = await onGenerate(profile);
    setDraft(next);
    setStep(4);
  };

  const previewStartingChapter = async () => {
    const next = await onGenerate(profile);
    setDraft(next);
    setEditingStartingChapterOnly(false);
    setStep(4);
  };

  const activatePlan = async () => {
    if (!draft) return;
    await onSave({ ...draft, status: "active", updatedAt: new Date().toISOString() });
    onClose();
  };

  const updateDraftProfile = <Key extends keyof SpanishJourneyGoalProfile>(
    key: Key,
    value: SpanishJourneyGoalProfile[Key]
  ) => {
    setProfile((current) => ({ ...current, [key]: value }));
    setDraft((current) =>
      current
        ? {
            ...current,
            profile: { ...current.profile, [key]: value },
            updatedAt: new Date().toISOString(),
          }
        : current
    );
  };

  const questions = [
    {
      eyebrow: "Your outcome",
      title: "What do you want to be able to do?",
      body: "Choose the result that matters most right now.",
      content: (
        <div className="space-y-3">
          <ChoiceGrid
            options={GOAL_OPTIONS}
            value={profile.goal}
            onChange={(goal) => setProfile((current) => ({ ...current, goal }))}
          />
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-white/55">
              A specific situation <span className="font-medium text-white/30">Optional</span>
            </span>
            <input
              value={profile.goalDetail || ""}
              onChange={(event) =>
                setProfile((current) => ({ ...current, goalDetail: event.target.value }))
              }
              maxLength={160}
              placeholder="For example: contribute during weekly team meetings"
              className="h-12 w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 text-sm text-white outline-none placeholder:text-white/30 focus:border-orange-300/55"
            />
          </label>
        </div>
      ),
    },
    {
      eyebrow: "Your starting point",
      title: "Choose the first chapter in your route",
      body: "Choose a reviewed unit, then the exact chapter where you want Chicky to begin.",
      content: (
        <StartingPointPicker
          profile={profile}
          onChange={(startingChapterId, ability) =>
            setProfile((current) => ({
              ...current,
              startingChapterId,
              ability,
              timeAvailable: "one_month",
            }))
          }
        />
      ),
    },
    {
      eyebrow: "How Chicky teaches",
      title: "How should Chicky teach you?",
      body: "Choose a default teaching move. The reviewed chapter still controls what you learn.",
      content: (
        <ChoiceGrid
          options={TEACHING_APPROACH_OPTIONS}
          value={profile.teachingApproach}
          onChange={(teachingApproach) =>
            setProfile((current) => ({ ...current, teachingApproach }))
          }
        />
      ),
    },
    {
      eyebrow: "When it gets difficult",
      title: "What should Chicky do when you’re stuck?",
      body: "Chicky can change the teaching move without abandoning the concept.",
      content: (
        <ChoiceGrid
          options={STUCK_STRATEGY_OPTIONS}
          value={profile.stuckStrategy}
          onChange={(stuckStrategy) => setProfile((current) => ({ ...current, stuckStrategy }))}
        />
      ),
    },
  ];

  const question = questions[step];
  const draftStartingChapter = draft ? getSpanishJourneyStartingChapter(draft.profile) : null;

  return (
    <CinematicModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="3xl"
      titleId="spanish-journey-planner-title"
      contentClassName="p-0"
    >
      <div className="border-b border-white/10 px-5 py-5 pr-14 sm:px-6">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/80">
          <Route className="h-3.5 w-3.5" aria-hidden="true" />
          Reviewed Journey Planner
        </div>
        <h2
          id="spanish-journey-planner-title"
          className="mt-1 text-xl font-black text-white sm:text-2xl"
        >
          {step === 4 ? draft?.title || "Your Spanish plan" : question.title}
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-white/50">
          {step === 4
            ? `This is your next focus route, not the whole course. All ${journey.chapters.length} reviewed chapters remain available in your Journey.`
            : question.body}
        </p>
      </div>

      {step < 4 ? (
        <div className="p-5 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <span className="text-xs font-bold text-white/40">
              {editingStartingChapterOnly ? "Starting chapter" : question.eyebrow}
            </span>
            <span className="font-mono text-xs text-white/35">
              {editingStartingChapterOnly ? "Choose one" : `${step + 1} / 4`}
            </span>
          </div>
          {question.content}
          <div className="mt-6 flex items-center justify-between gap-3 border-t border-white/10 pt-5">
            <button
              type="button"
              onClick={handleBack}
              className={cinematicModalSecondaryButtonClass}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back
            </button>
            {editingStartingChapterOnly ? (
              <button
                type="button"
                onClick={() => void previewStartingChapter()}
                disabled={generating}
                className={cinematicModalPrimaryButtonClass}
              >
                {generating ? (
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Gauge className="h-4 w-4" aria-hidden="true" />
                )}
                {generating
                  ? "Building route preview…"
                  : `Preview route from Chapter ${getSpanishJourneyStartingChapter(profile).chapterNumber}`}
              </button>
            ) : step < 3 ? (
              <button
                type="button"
                onClick={() => setStep((current) => current + 1)}
                className={cinematicModalPrimaryButtonClass}
              >
                Continue
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void createPlan()}
                disabled={generating}
                className={cinematicModalPrimaryButtonClass}
              >
                {generating ? (
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {generating ? "Designing your route…" : "Build my plan"}
              </button>
            )}
          </div>
        </div>
      ) : draft ? (
        <div className="p-5 sm:p-6">
          <div className="mb-5 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-white/10 bg-white/[0.025] p-3">
              <p className="text-lg font-black text-white">{draft.estimatedSessions}</p>
              <p className="text-xs font-semibold text-white/40">reviewed sessions</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.025] p-3">
              <p className="truncate text-sm font-black text-white">
                {SPANISH_GOAL_LABELS[draft.profile.goal]}
              </p>
              <p className="text-xs font-semibold text-white/40">
                {SPANISH_TEACHING_APPROACH_LABELS[draft.profile.teachingApproach]}
              </p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.025] p-3">
              <p className="truncate text-sm font-black text-white">
                Chapter {draftStartingChapter?.chapterNumber}: {draftStartingChapter?.title}
              </p>
              <p className="text-xs font-semibold text-white/40">route begins here</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-amber-300/25 bg-amber-400/10 p-3 text-xs font-semibold text-amber-100">
              {error}
            </div>
          )}

          <section className="mb-5 rounded-lg border border-white/10 bg-[#17181e] p-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div>
                <p className="text-[10px] font-black uppercase text-orange-300/75">
                  How Chicky teaches me
                </p>
                <h3 className="mt-1 text-sm font-black text-white">
                  Editable coaching preferences
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-white/40">
                  These change Chicky’s teaching moves, not the reviewed chapter objective.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingCoaching((current) => !current)}
                aria-expanded={editingCoaching}
                className="mt-2 min-h-9 shrink-0 rounded-lg border border-white/10 px-3 text-xs font-bold text-white/60 hover:bg-white/[0.05] hover:text-white sm:mt-0"
              >
                {editingCoaching ? "Done" : "Edit teaching approach"}
              </button>
            </div>

            {editingCoaching ? (
              <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
                <PreferenceChoices
                  label="Teaching approach"
                  options={Object.entries(SPANISH_TEACHING_APPROACH_LABELS).map(
                    ([value, label]) => ({
                      value: value as SpanishJourneyGoalProfile["teachingApproach"],
                      label,
                    })
                  )}
                  value={draft.profile.teachingApproach}
                  onChange={(value) => updateDraftProfile("teachingApproach", value)}
                />
                <PreferenceChoices
                  label="When I get stuck"
                  options={Object.entries(SPANISH_STUCK_STRATEGY_LABELS).map(([value, label]) => ({
                    value: value as SpanishJourneyGoalProfile["stuckStrategy"],
                    label,
                  }))}
                  value={draft.profile.stuckStrategy}
                  onChange={(value) => updateDraftProfile("stuckStrategy", value)}
                />
                <PreferenceChoices
                  label="Corrections"
                  options={Object.entries(SPANISH_CORRECTION_TIMING_LABELS).map(
                    ([value, label]) => ({
                      value: value as SpanishJourneyGoalProfile["correctionTiming"],
                      label,
                    })
                  )}
                  value={draft.profile.correctionTiming}
                  onChange={(value) => updateDraftProfile("correctionTiming", value)}
                />
                <PreferenceChoices
                  label="Language support"
                  options={Object.entries(SPANISH_LANGUAGE_SUPPORT_LABELS).map(
                    ([value, label]) => ({
                      value: value as SpanishJourneyGoalProfile["languageSupport"],
                      label,
                    })
                  )}
                  value={draft.profile.languageSupport}
                  onChange={(value) => updateDraftProfile("languageSupport", value)}
                />
                <PreferenceChoices
                  label="Challenge"
                  options={Object.entries(SPANISH_CHALLENGE_LEVEL_LABELS).map(([value, label]) => ({
                    value: value as SpanishJourneyGoalProfile["challengeLevel"],
                    label,
                  }))}
                  value={draft.profile.challengeLevel}
                  onChange={(value) => updateDraftProfile("challengeLevel", value)}
                />
              </div>
            ) : (
              <dl className="mt-4 grid gap-x-5 gap-y-3 border-t border-white/10 pt-4 sm:grid-cols-2">
                {[
                  ["Teaching", SPANISH_TEACHING_APPROACH_LABELS[draft.profile.teachingApproach]],
                  ["When stuck", SPANISH_STUCK_STRATEGY_LABELS[draft.profile.stuckStrategy]],
                  ["Corrections", SPANISH_CORRECTION_TIMING_LABELS[draft.profile.correctionTiming]],
                  [
                    "Language support",
                    SPANISH_LANGUAGE_SUPPORT_LABELS[draft.profile.languageSupport],
                  ],
                  ["Challenge", SPANISH_CHALLENGE_LEVEL_LABELS[draft.profile.challengeLevel]],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[10px] font-black uppercase text-white/35">{label}</dt>
                    <dd className="mt-1 text-xs font-bold leading-relaxed text-white/75">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] font-black uppercase text-orange-300/75">
                Chicky&apos;s recommended focus
              </p>
              <p className="mt-1 text-xs text-white/40">
                This is a suggestion, not a locked course. You can choose any unlocked chapter from
                the full Journey.
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenPath}
              className="inline-flex min-h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.025] px-3 text-xs font-bold text-white/60 transition-colors hover:bg-white/[0.055] hover:text-white"
            >
              <MapIcon className="h-3.5 w-3.5 text-orange-300" aria-hidden="true" />
              View full 90-chapter course
            </button>
          </div>

          <div className="space-y-2">
            {draft.items.map((item, index) => {
              const chapter = chapterById.get(item.chapterId);
              return (
                <div
                  key={item.chapterId}
                  className="grid gap-3 rounded-lg border border-orange-300/15 bg-orange-500/[0.025] p-3 sm:grid-cols-[2.5rem_minmax(0,1fr)] sm:items-start"
                >
                  <div>
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-orange-300/35 bg-orange-500/10 text-sm font-black text-orange-200">
                      {index + 1}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-bold text-white">
                        Chapter {chapter?.chapterNumber}: {chapter?.title}
                      </p>
                      <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-bold text-white/40">
                        {chapter?.cefrLevel}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-white/45">
                      {chapter?.newConcept
                        ? `New: ${chapter.newConcept}`
                        : `Focus: ${chapter?.coreForms || "reviewed chapter forms"}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={openStartingChapter}
                className={cinematicModalSecondaryButtonClass}
              >
                <Gauge className="h-4 w-4" aria-hidden="true" />
                Choose starting chapter
              </button>
              <button
                type="button"
                onClick={beginAgain}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 text-xs font-bold text-white/45 transition-colors hover:bg-white/[0.04] hover:text-white/75"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Change goal or all answers
              </button>
            </div>
            <button
              type="button"
              onClick={() => void activatePlan()}
              disabled={saving}
              className={cinematicModalPrimaryButtonClass}
            >
              {saving
                ? "Saving…"
                : existingPlan?.status === "active"
                  ? "Save changes"
                  : "Start this plan"}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </CinematicModal>
  );
}
